import { isAiNavigationPath } from '../data/siteNavigation.js';

// Entry points the model can attach to an Ask answer when the visitor asks
// about the site itself rather than about Henry. The model emits an
// [[ACTIONS]] block after [[VIEW]] and before [[FOLLOWUPS]]; the frontend
// parses it into buttons, links, and embedded components, and strips it from
// the visible answer. Everything here is validated so model output can never
// inject markup or navigate the visitor anywhere unexpected.

export const ASK_ACTIONS_MARKER = '[[ACTIONS]]';

const SAFE_ACTION_NAMES = new Set(['search', 'ask', 'view', 'views']);
const SAFE_COMPONENT_IDS = new Set(['retrieval-lab']);
const MAX_ACTIONS = 6;
const SITE_ORIGINS = new Set([
  'henryw.me',
  'www.henryw.me',
  'localhost',
  '127.0.0.1',
]);
const SITE_PATH_ALIASES = new Map([
  ['/scrollytelling-engine', '/scrollytelling'],
]);

function unescapeModelLinkSyntax(value) {
  return String(value || '')
    .replace(/\\([()[\]])/g, '$1')
    .replace(/\\\//g, '/');
}

export function normalizeAskSitePath(value) {
  let candidate = unescapeModelLinkSyntax(value).trim();
  if (!candidate) return '';

  if (/^https?:\/\//i.test(candidate)) {
    try {
      const url = new URL(candidate);
      if (!SITE_ORIGINS.has(url.hostname.toLowerCase())) return '';
      candidate = url.hash.startsWith('#/')
        ? url.hash.slice(1)
        : `${url.pathname}${url.search}${url.hash}`;
    } catch {
      return '';
    }
  } else if (candidate.startsWith('#/')) {
    candidate = candidate.slice(1);
  }

  const suffixAt = candidate.search(/[?#]/);
  const base = suffixAt < 0 ? candidate : candidate.slice(0, suffixAt);
  const suffix = suffixAt < 0 ? '' : candidate.slice(suffixAt);
  const canonical = SITE_PATH_ALIASES.get(base) || base;
  return `${canonical}${suffix}`;
}

// Only site-internal paths: a single leading slash, then safe path characters.
export function safeSitePath(path) {
  const value = normalizeAskSitePath(path);
  if (!value.startsWith('/') || value.startsWith('//')) return '';
  if (/[:\\\s]/.test(value)) return '';
  if (!/^\/[a-z0-9_\-/?=.#]*$/i.test(value)) return '';
  if (!isAiNavigationPath(value)) return '';
  return value;
}

export function parseAskActions(answer) {
  const raw = String(answer || '');
  const idx = raw.lastIndexOf(ASK_ACTIONS_MARKER);
  if (idx < 0) return [];
  const block = raw.slice(idx + ASK_ACTIONS_MARKER.length).split('[[FOLLOWUPS]]')[0];
  const actions = [];
  for (const line of block.split('\n')) {
    const entry = line.trim();
    if (!entry) continue;
    const actionMatch = entry.match(/^action\s*:\s*([a-z-]+)$/i);
    if (actionMatch && SAFE_ACTION_NAMES.has(actionMatch[1].toLowerCase())) {
      actions.push({ type: 'action', action: actionMatch[1].toLowerCase() });
      continue;
    }
    const componentMatch = entry.match(/^component\s*:\s*([a-z-]+)$/i);
    if (componentMatch && SAFE_COMPONENT_IDS.has(componentMatch[1].toLowerCase())) {
      actions.push({ type: 'component', id: componentMatch[1].toLowerCase() });
      continue;
    }
    const linkMatch = entry.match(/^link\s*:\s*([^|]+)\s*\|\s*(\S+)$/i);
    if (linkMatch) {
      const path = safeSitePath(linkMatch[2]);
      const label = String(linkMatch[1]).replace(/\s+/g, ' ').trim().slice(0, 60);
      if (path && label) actions.push({ type: 'link', label, path });
    }
  }
  return actions.slice(0, MAX_ACTIONS);
}

// Actions shown beside an Ask answer. When the answer carries a [[VIEW]]
// offer, stray actions from the model duplicate the offer's own "Explore the
// story" entry point (and can only belong to a site-feature answer, which the
// marker rules out), so they are dropped from the action row.
export function askActionsForDisplay(answer, routing) {
  if (routing === 'offer') return [];
  return parseAskActions(answer);
}

export function stripAskActions(answer) {
  const raw = String(answer || '');
  const idx = raw.lastIndexOf(ASK_ACTIONS_MARKER);
  if (idx < 0) return raw;
  const tail = raw.indexOf('[[FOLLOWUPS]]', idx);
  const suffix = tail >= 0 ? `\n${raw.slice(tail)}` : '';
  return `${raw.slice(0, idx).trimEnd()}${suffix}`.trim();
}

// Escape everything, then rebuild only the site-internal markdown-style links
// the model is allowed to emit, e.g. [Explore the Builds](/projects).
export function renderAskAnswerLinks(text) {
  const escaped = unescapeModelLinkSyntax(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
  return escaped.replace(/\[([^\]\n]{1,80})\]\(([^()\s]*(?:\([^)]*\)[^()\s]*)*)\)/g, (full, label, path) => {
    const safe = safeSitePath(path);
    const safeLabel = label.replace(/[<>&"]/g, '').slice(0, 80);
    if (!safeLabel) return '';
    // Ask only supports site links. If the model invents a route or emits an
    // external URL, keep the readable label and discard the broken syntax.
    if (!safe) return safeLabel;
    return `<a class="ask-inline-link" href="#${safe}">${safeLabel}</a>`;
  });
}

// Follow-ups are actions that submit a new question. A model may still wrap a
// page name in Markdown, so reduce links to their readable label before the
// question becomes a button or is sent back to Ask.
export function plainAskFollowUp(text) {
  return unescapeModelLinkSyntax(text)
    .replace(/\[([^\]\n]{1,120})\]\([^)\n]+\)/g, '$1')
    .replace(/\s+/g, ' ')
    .trim();
}
